import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Sparkle, TrendUp, Target, Rocket, ArrowRight, Check, Star, User, Play, MagnifyingGlass, Lightning, ShieldCheck, Clock, ChartLineUp, FileText, Users } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useTheme } from '@/contexts/ThemeContext';
import ThemeToggle from '@/components/ThemeToggle';
import WaitlistSection from '@/components/WaitlistSection';
import AnimatedCounter from '@/components/AnimatedCounter';
import { useAnimationVariants } from '@/hooks/useAnimations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Landing() {
  const { isDark } = useTheme();
  const [stats, setStats] = useState({ users: 0, seo_pages: 0, satisfaction: 98 });
  const { prefersReducedMotion, buttonHover, buttonTap } = useAnimationVariants();
  
  // Parallax effect
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, prefersReducedMotion ? 0 : 150]);
  const parallaxOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Fallback stats
      setStats({ users: 135, seo_pages: 1326, satisfaction: 98 });
    }
  };

  const steps = [
    {
      number: '01',
      title: 'Décrivez votre activité',
      description: 'Indiquez votre métier et votre zone géographique',
      icon: MagnifyingGlass,
    },
    {
      number: '02',
      title: 'L\'IA travaille pour vous',
      description: 'Génération automatique de pages SEO, publicités et vidéos',
      icon: Sparkle,
    },
    {
      number: '03',
      title: 'Recevez des clients',
      description: 'Votre trafic et vos demandes augmentent automatiquement',
      icon: TrendUp,
    },
  ];

  const microCopy = [
    { text: 'Aucun paiement requis', icon: ShieldCheck },
    { text: 'Résultats en quelques minutes', icon: Clock },
    { text: 'Sans compétences techniques', icon: Lightning },
  ];

  const results = [
    { value: stats.seo_pages, label: 'Pages SEO', sublabel: 'générées par notre IA', icon: FileText },
    { value: stats.users, label: 'Utilisateurs', sublabel: 'nous font confiance', icon: Users },
    { value: stats.satisfaction, label: '% Satisfaction', sublabel: 'clients satisfaits', icon: Star },
  ];

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#050508]' : 'bg-white'}`}>
      {/* Navigation Premium - Mobile Optimized with Safe Area */}
      <nav className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-2xl transition-colors duration-300 ${
        isDark 
          ? 'border-b border-white/[0.04] bg-[#050508]/80' 
          : 'border-b border-gray-100 bg-white/80'
      }`} style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-2 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center shadow-lg shadow-violet-500/25">
              <Sparkle size={16} weight="fill" className="text-white" />
            </div>
            <span className={`text-lg sm:text-2xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
              SIMBLYO
            </span>
          </Link>
          <div className="flex items-center gap-1.5 sm:gap-4">
            <ThemeToggle />
            <Link to="/login">
              <Button 
                variant="ghost" 
                size="sm"
                className={`rounded-lg px-2 sm:px-4 text-xs sm:text-sm ${isDark ? 'text-white/70 hover:text-white hover:bg-white/[0.05]' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
                data-testid="landing-login-btn"
              >
                <span className="hidden sm:inline">Connexion</span>
                <span className="sm:hidden">Login</span>
              </Button>
            </Link>
            <Link to="/register">
              <Button data-testid="landing-register-btn" className="btn-premium px-2.5 sm:px-5 py-1.5 sm:py-2.5 text-xs sm:text-sm whitespace-nowrap">
                <span className="hidden sm:inline">Essai gratuit 7 jours</span>
                <span className="sm:hidden">Essai 7j</span>
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section - Impact Maximum */}
      <section ref={heroRef} className="relative pt-32 sm:pt-36 md:pt-44 pb-16 sm:pb-20 md:pb-28 px-4 sm:px-6 overflow-hidden">
        {/* Background Effects with Parallax */}
        <motion.div 
          className="absolute inset-0 overflow-hidden pointer-events-none"
          style={{ y: parallaxY }}
        >
          <div className={`absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full blur-[120px] ${
            isDark ? 'bg-violet-600/20' : 'bg-violet-400/10'
          }`} />
          <div className={`absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full blur-[100px] ${
            isDark ? 'bg-pink-600/15' : 'bg-pink-400/10'
          }`} />
        </motion.div>

        <motion.div 
          className="max-w-5xl mx-auto text-center relative z-10"
          style={{ opacity: parallaxOpacity }}
        >
          {/* Badge */}
          <motion.div 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className={`inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full mb-8 sm:mb-10 ${
              isDark 
                ? 'bg-white/[0.04] border border-white/[0.08]' 
                : 'bg-violet-50 border border-violet-100'
            }`}
          >
            <Sparkle size={16} weight="fill" className="text-violet-500" />
            <span className={`text-sm font-medium ${isDark ? 'text-white/80' : 'text-violet-700'}`}>
              Assistant marketing propulsé par l'IA
            </span>
          </motion.div>
          
          {/* Titre Principal - TRÈS GRAND */}
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-extrabold tracking-tight mb-6 sm:mb-8 leading-[1.02] ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}
          >
            Lancez des campagnes
            <span className="block gradient-text-hero mt-2">
              rentables en 1 clic
            </span>
          </motion.h1>
          
          {/* Sous-titre */}
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className={`text-lg sm:text-xl md:text-2xl mb-10 sm:mb-12 leading-relaxed max-w-3xl mx-auto font-normal ${
              isDark ? 'text-white/55' : 'text-gray-500'
            }`}
          >
            SEO, publicité et contenu automatisés pour
            <span className={isDark ? 'text-white/80' : 'text-gray-700'}> générer des clients sans effort</span>
          </motion.p>
          
          {/* CTAs Premium with micro-animations */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5 mb-10 sm:mb-14"
          >
            <Link to="/register" className="w-full sm:w-auto">
              <motion.div
                whileHover={buttonHover}
                whileTap={buttonTap}
              >
                <Button size="lg" className="btn-premium w-full sm:w-auto px-8 sm:px-10 py-6 sm:py-7 text-base sm:text-lg rounded-2xl glow-intense font-semibold" data-testid="hero-cta-btn">
                  Essai gratuit 7 jours
                  <ArrowRight className="ml-3" size={20} weight="bold" />
                </Button>
              </motion.div>
            </Link>
            <Link to="/pricing" className="w-full sm:w-auto">
              <motion.div
                whileHover={buttonHover}
                whileTap={buttonTap}
              >
                <Button 
                  size="lg" 
                  variant="outline" 
                  className={`w-full sm:w-auto px-8 sm:px-10 py-6 sm:py-7 text-base sm:text-lg rounded-2xl btn-secondary font-medium`}
                  data-testid="hero-demo-btn"
                >
                  <Play className="mr-2" size={18} weight="fill" />
                  Voir la démo
                </Button>
              </motion.div>
            </Link>
          </motion.div>

          {/* Micro Copy - Reassurance */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-wrap items-center justify-center gap-4 sm:gap-8"
          >
            {microCopy.map((item, i) => (
              <div key={i} className={`flex items-center gap-2 text-sm ${isDark ? 'text-white/45' : 'text-gray-500'}`}>
                <item.icon size={16} weight="fill" className="text-violet-500" />
                <span>{item.text}</span>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      {/* Section Résultats - Stats Réelles avec Compteurs Animés */}
      <section className={`py-16 sm:py-20 px-4 sm:px-6 ${isDark ? 'bg-white/[0.02]' : 'bg-gray-50'}`}>
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-3 gap-4 sm:gap-8">
            {results.map((result, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className={`text-3xl sm:text-5xl md:text-6xl font-extrabold mb-1 sm:mb-2 gradient-text`}>
                  <AnimatedCounter 
                    value={typeof result.value === 'number' ? result.value : 0} 
                    suffix={result.label.includes('%') ? '%' : '+'}
                  />
                </div>
                <div className={`text-sm sm:text-base font-semibold mb-0.5 ${isDark ? 'text-white/90' : 'text-gray-900'}`}>
                  {result.label}
                </div>
                <div className={`text-xs sm:text-sm ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                  {result.sublabel}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section Comment ça marche - 3 Étapes */}
      <section className="py-20 sm:py-28 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14 sm:mb-20">
            <motion.h2 
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-5 ${isDark ? 'text-white' : 'text-gray-900'}`}
            >
              Comment ça
              <span className="gradient-text ml-3">fonctionne ?</span>
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className={`text-base sm:text-lg max-w-xl mx-auto ${isDark ? 'text-white/50' : 'text-gray-500'}`}
            >
              3 étapes simples pour automatiser votre acquisition client
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className={`relative p-7 sm:p-9 rounded-3xl transition-all duration-300 hover-lift ${
                  isDark 
                    ? 'bg-white/[0.03] border border-white/[0.06] hover:border-violet-500/30' 
                    : 'bg-white border border-gray-100 shadow-sm hover:shadow-xl'
                }`}
              >
                <div className="flex items-start justify-between mb-5">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                    isDark 
                      ? 'bg-gradient-to-br from-violet-500/20 to-pink-500/20' 
                      : 'bg-violet-100'
                  }`}>
                    <step.icon size={26} weight="duotone" className="text-violet-500" />
                  </div>
                  <span className={`text-5xl sm:text-6xl font-extrabold ${isDark ? 'text-white/[0.06]' : 'text-gray-100'}`}>
                    {step.number}
                  </span>
                </div>
                <h3 className={`text-xl sm:text-2xl font-bold mb-2.5 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {step.title}
                </h3>
                <p className={`text-base leading-relaxed ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section Premium */}
      <section className="py-20 sm:py-28 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative overflow-hidden rounded-[2rem] sm:rounded-[2.5rem] p-10 sm:p-14 md:p-16 text-center"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-violet-600 via-purple-600 to-pink-600"></div>
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLW9wYWNpdHk9IjAuMDUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-50"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
            
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm mb-6 sm:mb-8">
                <Rocket size={18} weight="fill" className="text-white" />
                <span className="text-sm text-white/90 font-medium">Commencez gratuitement</span>
              </div>
              
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight">
                Prêt à générer
                <span className="block">vos premiers clients ?</span>
              </h2>
              
              <p className="text-white/75 text-base sm:text-lg md:text-xl mb-8 sm:mb-10 max-w-xl mx-auto leading-relaxed">
                Automatisez votre acquisition client avec SIMBLYO
              </p>
              
              <Link to="/register">
                <Button 
                  size="lg" 
                  className="bg-white text-violet-600 px-10 sm:px-12 py-6 sm:py-7 text-base sm:text-lg rounded-2xl font-bold hover:bg-white/95 transition-all shadow-2xl hover:scale-105"
                  data-testid="footer-cta-btn"
                >
                  Créer une campagne maintenant
                  <ArrowRight className="ml-3" size={20} weight="bold" />
                </Button>
              </Link>
              
              <p className="text-white/50 text-sm mt-6">
                Aucune carte bancaire requise • Configuration en 2 minutes
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section Waitlist - Remplace les avis */}
      <WaitlistSection isDark={isDark} />

      {/* Footer Premium */}
      <footer className={`py-14 sm:py-16 px-4 sm:px-6 ${
        isDark 
          ? 'border-t border-white/[0.04]' 
          : 'border-t border-gray-100 bg-white'
      }`}>
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-12">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center">
                  <Sparkle size={18} weight="fill" className="text-white" />
                </div>
                <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  SIMBLYO
                </span>
              </div>
              <p className={`text-sm leading-relaxed max-w-xs ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                Votre assistant IA qui génère des clients automatiquement via Google, Facebook et Instagram.
              </p>
            </div>
            <div>
              <h4 className={`font-bold mb-4 text-sm uppercase tracking-wider ${isDark ? 'text-white/80' : 'text-gray-900'}`}>
                Produit
              </h4>
              <ul className={`space-y-3 text-sm ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                <li><Link to="/pricing" className="hover:text-violet-500 transition-colors">Tarifs</Link></li>
                <li><Link to="/register" className="hover:text-violet-500 transition-colors">Commencer</Link></li>
              </ul>
            </div>
            <div>
              <h4 className={`font-bold mb-4 text-sm uppercase tracking-wider ${isDark ? 'text-white/80' : 'text-gray-900'}`}>
                Légal
              </h4>
              <ul className={`space-y-3 text-sm ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                <li><Link to="/cgu" className="hover:text-violet-500 transition-colors">CGU</Link></li>
                <li><Link to="/privacy" className="hover:text-violet-500 transition-colors">Confidentialité</Link></li>
                <li><Link to="/cookies" className="hover:text-violet-500 transition-colors">Cookies</Link></li>
              </ul>
            </div>
            <div>
              <h4 className={`font-bold mb-4 text-sm uppercase tracking-wider ${isDark ? 'text-white/80' : 'text-gray-900'}`}>
                Contact
              </h4>
              <ul className={`space-y-3 text-sm ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                <li>contact@simblyo.com</li>
              </ul>
            </div>
          </div>
          <div className={`border-t pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm ${
            isDark 
              ? 'border-white/[0.04] text-white/30' 
              : 'border-gray-100 text-gray-400'
          }`}>
            <p>© 2026 SIMBLYO. Tous droits réservés.</p>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5">
                <ShieldCheck size={14} weight="fill" className="text-green-500" />
                Données sécurisées
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
